import { useEffect } from 'react'

import { mediaURL } from '../../api/objects-write'
import type { MediaAsset } from '../../api/objects'
import { t } from '../../i18n'

/**
 * One file, as large as the screen allows.
 *
 * The grid on a card shows thumbnails, and a thumbnail is useless for the two
 * things anybody opens a photograph for: seeing whether the kitchen is really
 * like that, and playing a video. A ninety-pixel box with a video's own controls
 * crammed into it is a control nobody can hit.
 *
 * So this is a plain overlay: the file at full size, arrows to the next one, and
 * escape to leave. It is not a gallery library -- there is one thing to do here
 * and adding zoom, captions and transitions would be building a product nobody
 * asked for on top of the one that was.
 */
export function MediaViewer({
  assets,
  index,
  onClose,
  onMove,
}: {
  readonly assets: readonly MediaAsset[]
  readonly index: number
  readonly onClose: () => void
  readonly onMove: (next: number) => void
}) {
  const asset = assets[index]

  // Escape closes and the arrows move, because that is what every viewer does
  // and a person will try it before looking for a button.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') onMove((index + 1) % assets.length)
      if (event.key === 'ArrowLeft') onMove((index - 1 + assets.length) % assets.length)
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [assets.length, index, onClose, onMove])

  if (asset === undefined) return null

  return (
    <div
      className="viewer"
      role="dialog"
      aria-modal="true"
      aria-label={t('media.viewer')}
      // Clicking the backdrop closes; clicking the file itself does not, or
      // pressing play would shut the thing you pressed it in.
      onClick={onClose}
    >
      <div className="viewer__stage" onClick={(event) => event.stopPropagation()}>
        {asset.kind === 'video' ? (
          <video
            className="viewer__media"
            src={mediaURL(asset.id)}
            controls
            autoPlay
            // The server answers ranges, so the browser fetches what it needs to
            // start rather than the whole file.
            preload="auto"
          />
        ) : (
          <img className="viewer__media" src={mediaURL(asset.id)} alt={asset.caption} />
        )}

        {asset.caption === '' ? null : <p className="viewer__caption">{asset.caption}</p>}
      </div>

      {assets.length < 2 ? null : (
        <>
          <button
            type="button"
            className="viewer__step viewer__step--back"
            aria-label={t('media.previous')}
            onClick={(event) => {
              event.stopPropagation()
              onMove((index - 1 + assets.length) % assets.length)
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="viewer__step viewer__step--forward"
            aria-label={t('media.next')}
            onClick={(event) => {
              event.stopPropagation()
              onMove((index + 1) % assets.length)
            }}
          >
            ›
          </button>
          <span className="viewer__count">
            {index + 1} / {assets.length}
          </span>
        </>
      )}

      <button type="button" className="viewer__close" aria-label={t('common.close')} onClick={onClose}>
        ×
      </button>
    </div>
  )
}
